import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ToolContext } from "@/components/tools/ToolContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Globe, CheckCircle, XCircle, AlertTriangle, Link2, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

type Language = 'es' | 'en';

interface URLAnalysis {
  url: string;
  score: number;
  issues: {
    type: 'error' | 'warning' | 'success';
    message: string;
    impact: 'high' | 'medium' | 'low';
  }[];
  structure: {
    protocol: string;
    domain: string;
    path: string;
    parameters: string[];
    length: number;
  };
  seoFactors: {
    name: string;
    status: 'good' | 'warning' | 'error';
    score: number;
    description: string;
  }[];
  recommendations: string[];
}

const URLAnalyzerPage = () => {
  const { language, url: canonicalUrl, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<URLAnalysis | null>(null);


  const content = {
    es: {
      title: "Analizador de URLs SEO - Luis ROC | Optimización Web",
      description: "Analiza la estructura SEO de tus URLs. Evalúa longitud, estructura, palabras clave y factores de optimización para buscadores.",
      pageTitle: "Analizador de URLs SEO",
      subtitle: "Evalúa y optimiza la estructura SEO de tus URLs",
      urlLabel: "URL a analizar",
      urlPlaceholder: "https://tudominio.com/pagina-ejemplo",
      analyzeButton: "Analizar URL",
      analyzing: "Analizando...",
      results: "Análisis de URL",
      seoScore: "Puntuación SEO",
      urlStructure: "Estructura de URL",
      seoFactors: "Factores SEO",
      issues: "Problemas Detectados",
      recommendations: "Recomendaciones",
      protocol: "Protocolo",
      domain: "Dominio",
      path: "Ruta",
      parameters: "Parámetros",
      length: "Longitud",
      good: "Bueno",
      warning: "Advertencia",
      error: "Error",
      highImpact: "Alto Impacto",
      mediumImpact: "Impacto Medio",
      lowImpact: "Bajo Impacto"
    },
    en: {
      title: "SEO URL Analyzer - Luis ROC | Web Optimization",
      description: "Analyze the SEO structure of your URLs. Evaluate length, structure, keywords and optimization factors for search engines.",
      pageTitle: "SEO URL Analyzer",
      subtitle: "Evaluate and optimize the SEO structure of your URLs",
      urlLabel: "URL to analyze",
      urlPlaceholder: "https://yourdomain.com/example-page",
      analyzeButton: "Analyze URL",
      analyzing: "Analyzing...",
      results: "URL Analysis",
      seoScore: "SEO Score",
      urlStructure: "URL Structure",
      seoFactors: "SEO Factors",
      issues: "Issues Detected",
      recommendations: "Recommendations",
      protocol: "Protocol",
      domain: "Domain",
      path: "Path",
      parameters: "Parameters",
      length: "Length",
      good: "Good",
      warning: "Warning",
      error: "Error",
      highImpact: "High Impact",
      mediumImpact: "Medium Impact",
      lowImpact: "Low Impact"
    }
  };

  const currentContent = content[language];

  const handleAnalyze = async () => {
    if (!url.trim()) return;
    
    setIsAnalyzing(true);
    
    // Simulate URL analysis
    setTimeout(() => {
      try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname.split('/').filter(Boolean);
        const searchParams = Array.from(urlObj.searchParams.keys());
        
        const issues = [];
        const seoFactors = [];
        const recommendations = [];
        let score = 100;

        // Length analysis
        if (url.length > 100) {
          issues.push({
            type: 'warning' as const,
            message: language === 'es' ? 'URL muy larga (>100 caracteres)' : 'URL too long (>100 characters)',
            impact: 'medium' as const
          });
          score -= 10;
        }

        // HTTPS analysis
        if (urlObj.protocol === 'https:') {
          seoFactors.push({
            name: 'HTTPS',
            status: 'good' as const,
            score: 100,
            description: language === 'es' ? 'Protocolo seguro detectado' : 'Secure protocol detected'
          });
        } else {
          issues.push({
            type: 'error' as const,
            message: language === 'es' ? 'URL no usa HTTPS' : 'URL does not use HTTPS',
            impact: 'high' as const
          });
          score -= 20;
        }

        // Path structure
        const hasKeywords = pathSegments.some(segment => 
          segment.length > 3 && !segment.match(/^\d+$/)
        );
        
        if (hasKeywords) {
          seoFactors.push({
            name: language === 'es' ? 'Palabras clave en URL' : 'Keywords in URL',
            status: 'good' as const,
            score: 90,
            description: language === 'es' ? 'URL contiene palabras descriptivas' : 'URL contains descriptive words'
          });
        } else {
          seoFactors.push({
            name: language === 'es' ? 'Palabras clave en URL' : 'Keywords in URL',
            status: 'warning' as const,
            score: 60,
            description: language === 'es' ? 'URL podría ser más descriptiva' : 'URL could be more descriptive'
          });
          score -= 15;
        }

        // Parameters analysis
        if (searchParams.length > 3) {
          issues.push({
            type: 'warning' as const,
            message: language === 'es' ? 'Demasiados parámetros en URL' : 'Too many parameters in URL',
            impact: 'medium' as const
          });
          score -= 10;
        }

        // Generate recommendations
        if (score < 90) {
          recommendations.push(
            language === 'es' ? 'Simplifica la estructura de la URL' : 'Simplify URL structure'
          );
        }
        if (!urlObj.protocol.includes('https')) {
          recommendations.push(
            language === 'es' ? 'Migra a HTTPS para mayor seguridad' : 'Migrate to HTTPS for better security'
          );
        }

        const mockAnalysis: URLAnalysis = {
          url,
          score: Math.max(score, 0),
          issues,
          structure: {
            protocol: urlObj.protocol,
            domain: urlObj.hostname,
            path: urlObj.pathname,
            parameters: searchParams,
            length: url.length
          },
          seoFactors,
          recommendations
        };

        setAnalysis(mockAnalysis);
      } catch (error) {
        // Handle invalid URL
        setAnalysis({
          url,
          score: 0,
          issues: [{
            type: 'error',
            message: language === 'es' ? 'URL inválida' : 'Invalid URL',
            impact: 'high'
          }],
          structure: {
            protocol: '',
            domain: '',
            path: '',
            parameters: [],
            length: url.length
          },
          seoFactors: [],
          recommendations: [
            language === 'es' ? 'Introduce una URL válida' : 'Enter a valid URL'
          ]
        });
      }
      
      setIsAnalyzing(false);
    }, 2000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const meta = {
    es: {
      title: "Analizador URLs SEO - Luis ROC | Optimización Estructura Web",
      description: "Herramienta gratuita para analizar URLs SEO. Evalúa estructura, longitud, palabras clave y factores de optimización web.",
      url: "https://luisroc.com/herramientas/analizador-url"
    },
    en: {
      title: "SEO URL Analyzer - Luis ROC | Web Structure Optimization",
      description: "Free tool to analyze SEO URLs. Evaluate structure, length, keywords and web optimization factors.",
      url: "https://luisroc.com/tools/url-analyzer"
    }
  };

  const currentMeta = meta[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{currentMeta.title}</title>
        <meta name="description" content={currentMeta.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="keywords" content="analizador URL, SEO URL, estructura URL, optimización web" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
                <Link2 className="h-8 w-8 text-primary" />
                {currentContent.pageTitle}
              </h1>
              <p className="text-xl text-muted-foreground">
                {currentContent.subtitle}
              </p>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  {currentContent.pageTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.urlLabel}
                  </label>
                  <Input
                    placeholder={currentContent.urlPlaceholder}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>

                <Button 
                  onClick={handleAnalyze} 
                  disabled={isAnalyzing || !url.trim()}
                  className="w-full"
                >
                  {isAnalyzing ? currentContent.analyzing : currentContent.analyzeButton}
                </Button>
              </CardContent>
            </Card>

            {analysis && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      {currentContent.results}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">{currentContent.seoScore}</h3>
                        <span className={`text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                          {analysis.score}/100
                        </span>
                      </div>
                      <Progress value={analysis.score} className="h-2" />
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">{currentContent.urlStructure}</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium">{currentContent.protocol}:</span>
                            <span className="text-sm">{analysis.structure.protocol}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium">{currentContent.domain}:</span>
                            <span className="text-sm">{analysis.structure.domain}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm font-medium">{currentContent.length}:</span>
                            <Badge variant={analysis.structure.length > 100 ? "destructive" : "default"}>
                              {analysis.structure.length} chars
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium">{currentContent.path}:</span>
                            <p className="text-sm mt-1 p-2 bg-muted rounded font-mono break-all">
                              {analysis.structure.path || '/'}
                            </p>
                          </div>
                          {analysis.structure.parameters.length > 0 && (
                            <div>
                              <span className="text-sm font-medium">{currentContent.parameters}:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {analysis.structure.parameters.map((param, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {param}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {analysis.seoFactors.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">{currentContent.seoFactors}</h3>
                        <div className="space-y-3">
                          {analysis.seoFactors.map((factor, index) => (
                            <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(factor.status)}
                                <div>
                                  <p className="font-medium">{factor.name}</p>
                                  <p className="text-sm text-muted-foreground">{factor.description}</p>
                                </div>
                              </div>
                              <Badge variant={factor.status === 'good' ? 'default' : factor.status === 'warning' ? 'secondary' : 'destructive'}>
                                {factor.score}/100
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {analysis.issues.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">{currentContent.issues}</h3>
                        <div className="space-y-2">
                          {analysis.issues.map((issue, index) => (
                            <div key={index} className="flex items-start gap-2 p-3 border rounded-lg">
                              {issue.type === 'error' && <XCircle className="h-4 w-4 text-red-500 mt-0.5" />}
                              {issue.type === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />}
                              {issue.type === 'success' && <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />}
                              <div className="flex-1">
                                <p className="text-sm">{issue.message}</p>
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {issue.impact === 'high' ? currentContent.highImpact : 
                                   issue.impact === 'medium' ? currentContent.mediumImpact : currentContent.lowImpact}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {analysis.recommendations.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">{currentContent.recommendations}</h3>
                        <ul className="space-y-2">
                          {analysis.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <ToolContext tool="url-analyzer" language={language} />
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default URLAnalyzerPage;